import { useEffect } from 'react';
import { SEEDANCE_25_DRAFT_RESOLUTION, SEEDANCE_25_MODELS } from './consts/seedance-25.const';
import type { ParameterView } from '@/components/Recipe/Views/ParameterView/ParameterView';

export function isSeedance25Model(modelName: string | undefined): boolean {
  return modelName !== undefined && SEEDANCE_25_MODELS.has(modelName);
}

/**
 * Whether Draft mode is on for a Seedance 2.5 node. Reads the evaluated value, so a toggle wired in
 * from another node counts even when the saved param differs. Always false for other models.
 */
export function useIsSeedance25Draft(view: ParameterView, modelName: string | undefined): boolean {
  if (!isSeedance25Model(modelName) || !view.getParameterIds().includes('draft')) {
    return false;
  }
  return view.getParameterValue('draft')?.value === true;
}

/**
 * While Draft mode is on, keeps a Seedance 2.5 node's resolution at 480p: Fal renders drafts at 480p,
 * and credits are priced from resolution before the run. A wired-in resolution is left to server
 * validation, and is reset once it is collapsed back into the node.
 */
export function useSeedance25DraftResolution(view: ParameterView, modelName: string | undefined, isDraft: boolean) {
  const isSeedance25 = isSeedance25Model(modelName);
  const resolution =
    isSeedance25 && view.getParameterIds().includes('resolution')
      ? view.getParameterValue('resolution')?.value
      : undefined;
  const isResolutionExposed = isSeedance25 && view.isParameterExposed('resolution');

  useEffect(() => {
    if (isDraft && !isResolutionExposed && resolution !== SEEDANCE_25_DRAFT_RESOLUTION) {
      view.setParameterValue('resolution', { type: 'string', value: SEEDANCE_25_DRAFT_RESOLUTION });
    }
  }, [isDraft, isResolutionExposed, resolution, view]);
}
